import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@shared/lib';

export interface StatCardProps extends Omit<ComponentProps<'div'>, 'title'> {
  /** Caption under the value (e.g. "This month spending"). */
  label: ReactNode;
  /** Headline figure — plain text or an `<Amount>`. */
  value?: ReactNode;
  /** Optional icon, shown in the top-right corner. */
  icon?: ReactNode;
  /** Optional secondary line (e.g. a delta or date range). */
  hint?: ReactNode;
  /** Optional flag to render as an empty/nodata state. */
  isEmpty?: boolean;
}

/**
 * Dashboard KPI tile (Figma metric cards): a big value + caption on a pale-lime tile with a
 * corner icon. **Reusability:** any "big number + label" summary. **A11y:** value/label are
 * plain text; the corner icon is decorative (`aria-hidden`).
 */
export function StatCard({
  label,
  value,
  icon,
  hint,
  isEmpty,
  className,
  ...props
}: StatCardProps) {
  if (isEmpty) {
    return (
      <div
        data-slot="stat-card-empty"
        className={cn(
          'relative overflow-hidden rounded-3xl border-kpi-gradient bg-surface-kpi p-5 pt-10 min-h-28 sm:min-h-32.5 flex items-center justify-center shadow-2xs',
          className,
        )}
        {...props}
      >
        {icon ? (
          <div className="absolute top-0 right-0 w-31.25 h-19.5 pointer-events-none select-none">
            <svg
              width="130"
              height="45"
              viewBox="0 0 86 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_41_1824)">
                <path
                  d="M21.6734 21.6694L15.8176 10.3183C12.4075 3.70816 5.61095 -0.463601 -1.82677 -0.511906L-139.786 -1.40789C-144.726 -1.43997 -148.768 2.51555 -148.843 7.45487L-149.174 29.2053C-150.38 29.2053 -151.357 28.2283 -151.357 27.0231V-6.26103L92.9999 -5.50001V32.5H39.4476C31.963 32.5 25.1048 28.321 21.6734 21.6694Z"
                  fill="url(#paint0_linear_41_1824)"
                />
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_41_1824"
                  x1="-145.5"
                  y1="113.501"
                  x2="82.9998"
                  y2="-0.999298"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#E8EEC9" />
                  <stop offset="1" stopColor="#E2F6E0" />
                </linearGradient>
                <clipPath id="clip0_41_1824">
                  <rect width="86" height="32" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <span
              aria-hidden="true"
              className="absolute top-2.5 right-6 text-[#5d803d] flex items-center justify-center pointer-events-auto"
            >
              {icon}
            </span>
          </div>
        ) : null}
        <span className="text-center text-sm font-medium text-[#747474] px-4 leading-normal">
          {label}
        </span>
      </div>
    );
  }

  return (
    <div
      data-slot="stat-card"
      className={cn(
        'relative overflow-hidden rounded-lg border-kpi-gradient p-3.5 pb-3 pt-14 min-h-28 sm:p-5 sm:pb-4 sm:pt-10 sm:min-h-32.5 bg-surface-kpi flex flex-col justify-end',
        className,
      )}
      {...props}
    >
      {icon ? (
        <div className="absolute top-0 right-0 w-31.25 h-19.5 pointer-events-none select-none">
          <svg
            width="130"
            height="45"
            viewBox="0 0 86 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_41_1824)">
              <path
                d="M21.6734 21.6694L15.8176 10.3183C12.4075 3.70816 5.61095 -0.463601 -1.82677 -0.511906L-139.786 -1.40789C-144.726 -1.43997 -148.768 2.51555 -148.843 7.45487L-149.174 29.2053C-150.38 29.2053 -151.357 28.2283 -151.357 27.0231V-6.26103L92.9999 -5.50001V32.5H39.4476C31.963 32.5 25.1048 28.321 21.6734 21.6694Z"
                fill="url(#paint0_linear_41_1824)"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_41_1824"
                x1="-145.5"
                y1="113.501"
                x2="82.9998"
                y2="-0.999298"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#E8EEC9" />
                <stop offset="1" stopColor="#E2F6E0" />
              </linearGradient>
              <clipPath id="clip0_41_1824">
                <rect width="86" height="32" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span
            aria-hidden="true"
            className="absolute top-2.5 right-6 text-[#5d803d] flex items-center justify-center pointer-events-auto"
          >
            {icon}
          </span>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate font-grotesk text-2xl sm:text-[2.25rem] font-semibold leading-none text-brand-600 [&_[data-slot=amount-symbol]]:text-[18px]">
          {value}
        </span>
        <span className="text-xs sm:text-sm text-muted leading-tight">{label}</span>
        {hint ? <span className="truncate text-xs text-muted-soft">{hint}</span> : null}
      </div>
    </div>
  );
}
